import { useEffect, useState } from 'react';
import {
  CloudArrowDown, Database, Clock, Trash, Play, CheckCircle, Warning,
  ArrowsClockwise, FloppyDisk, DownloadSimple, ArrowCounterClockwise,
  UploadSimple, X,
} from '@phosphor-icons/react';

const API = process.env.REACT_APP_BACKEND_URL;

function fmtSize(b) {
  if (!b && b !== 0) return '—';
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
  if (b < 1024 * 1024 * 1024) return `${(b / 1024 / 1024).toFixed(1)} MB`;
  return `${(b / 1024 / 1024 / 1024).toFixed(2)} GB`;
}

function fmtDateTime(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  return d.toLocaleString('nl-NL', { dateStyle: 'medium', timeStyle: 'short' });
}

function relTime(iso) {
  if (!iso) return 'nooit';
  const d = new Date(iso);
  const diff = (Date.now() - d.getTime()) / 1000;
  if (diff < 60) return 'zojuist';
  if (diff < 3600) return `${Math.floor(diff / 60)} min geleden`;
  if (diff < 86400) return `${Math.floor(diff / 3600)} uur geleden`;
  return `${Math.floor(diff / 86400)} d geleden`;
}

export default function BackupsPage() {
  const [backups, setBackups] = useState([]);
  const [settings, setSettings] = useState(null);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(false);
  const [running, setRunning] = useState(false);
  const [savingSettings, setSavingSettings] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  // Restore modal state
  const [restoreModal, setRestoreModal] = useState(null); // {filename, preview} of null
  const [restoreLoading, setRestoreLoading] = useState(false);
  const [confirmText, setConfirmText] = useState('');
  const [uploading, setUploading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [b, s, h] = await Promise.all([
        fetch(`${API}/api/admin/backups`, { credentials: 'include' }),
        fetch(`${API}/api/admin/backups/settings`, { credentials: 'include' }),
        fetch(`${API}/api/admin/backups/history?limit=20`, { credentials: 'include' }),
      ]);
      if (b.ok) setBackups((await b.json()).backups || []);
      if (s.ok) setSettings(await s.json());
      if (h.ok) setHistory((await h.json()).history || []);
    } catch (e) {
      setError('Kon backup-data niet laden');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const runBackup = async () => {
    if (running) return;
    setRunning(true); setError(null); setSuccess(null);
    try {
      const r = await fetch(`${API}/api/admin/backups/run`, {
        method: 'POST', credentials: 'include',
      });
      if (!r.ok) {
        const err = await r.json().catch(() => ({ detail: r.statusText }));
        throw new Error(err.detail || 'Backup failed');
      }
      const res = await r.json();
      setSuccess(`Backup voltooid · ${res.collections} collecties · ${res.docs} docs · ${res.files_added || 0} bestanden · ${fmtSize(res.size_bytes)}`);
      await load();
    } catch (e) {
      setError(`Backup mislukt: ${e.message}`);
    } finally {
      setRunning(false);
    }
  };

  const deleteBackup = async (fn) => {
    if (!window.confirm(`Backup "${fn}" definitief verwijderen?`)) return;
    try {
      const r = await fetch(`${API}/api/admin/backups/${encodeURIComponent(fn)}`, {
        method: 'DELETE', credentials: 'include',
      });
      if (!r.ok) throw new Error('Delete failed');
      await load();
    } catch (e) {
      setError(`Verwijderen mislukt: ${e.message}`);
    }
  };

  const downloadBackup = (fn) => {
    window.open(`${API}/api/admin/backups/${encodeURIComponent(fn)}/download`, '_blank');
  };

  const openRestore = async (fn) => {
    setError(null); setSuccess(null); setConfirmText('');
    try {
      const r = await fetch(`${API}/api/admin/backups/${encodeURIComponent(fn)}/preview`, { credentials: 'include' });
      if (!r.ok) {
        const err = await r.json().catch(() => ({ detail: r.statusText }));
        throw new Error(err.detail || 'Preview mislukt');
      }
      const preview = await r.json();
      setRestoreModal({ filename: fn, preview });
    } catch (e) {
      setError(`Preview mislukt: ${e.message}`);
    }
  };

  const doRestore = async () => {
    if (!restoreModal) return;
    if (confirmText !== 'RESTORE') return;
    setRestoreLoading(true); setError(null); setSuccess(null);
    try {
      const r = await fetch(`${API}/api/admin/backups/${encodeURIComponent(restoreModal.filename)}/restore`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ confirm: 'RESTORE' }),
      });
      if (!r.ok) {
        const err = await r.json().catch(() => ({ detail: r.statusText }));
        throw new Error(err.detail || 'Restore mislukt');
      }
      const res = await r.json();
      setSuccess(
        `Restore voltooid · ${res.restored_collections} collecties · ${res.restored_docs} docs · ` +
        `${res.restored_files} bestanden · safety: ${res.safety_backup}`
      );
      setRestoreModal(null); setConfirmText('');
      await load();
      // hard reload after a moment so all other pages refresh their data
      setTimeout(() => window.location.reload(), 2500);
    } catch (e) {
      setError(`Restore mislukt: ${e.message}`);
    } finally {
      setRestoreLoading(false);
    }
  };

  const onUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.name.endsWith('.tar.gz')) {
      setError('Alleen .tar.gz bestanden toegestaan');
      return;
    }
    setUploading(true); setError(null); setSuccess(null);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const r = await fetch(`${API}/api/admin/backups/upload`, {
        method: 'POST', credentials: 'include', body: fd,
      });
      if (!r.ok) {
        const err = await r.json().catch(() => ({ detail: r.statusText }));
        throw new Error(err.detail || 'Upload mislukt');
      }
      const res = await r.json();
      setSuccess(`Upload geslaagd · ${res.filename} · ${res.total_docs} docs · ${fmtSize(res.size_bytes)}`);
      await load();
    } catch (err) {
      setError(`Upload mislukt: ${err.message}`);
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  const saveSettings = async () => {
    if (!settings) return;
    setSavingSettings(true); setError(null); setSuccess(null);
    try {
      const r = await fetch(`${API}/api/admin/backups/settings`, {
        method: 'PUT', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          enabled: settings.enabled,
          hour: settings.hour,
          retention_days: settings.retention_days,
        }),
      });
      if (!r.ok) throw new Error('Settings save failed');
      setSuccess('Instellingen opgeslagen');
      setSettings(await r.json());
    } catch (e) {
      setError(`Opslaan mislukt: ${e.message}`);
    } finally {
      setSavingSettings(false);
    }
  };

  return (
    <div className="p-6 lg:p-8 max-w-6xl mx-auto" data-testid="backups-page">
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-zinc-900 flex items-center gap-2">
            <CloudArrowDown size={24} weight="duotone" />
            Backups
          </h1>
          <p className="text-sm text-zinc-500 mt-1">
            Automatische nachtelijke backup van alle MongoDB collecties. Download regelmatig
            een kopie lokaal — backups in deze container zijn ook kwetsbaar bij redeploy.
          </p>
        </div>
        <button
          onClick={load}
          className="p-2 hover:bg-zinc-100 rounded transition-colors"
          data-testid="backups-refresh-btn"
          title="Vernieuwen"
        >
          <ArrowsClockwise size={18} className={loading ? 'animate-spin' : ''} />
        </button>
      </div>

      {/* Upload bar */}
      <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-sm flex items-center justify-between gap-3">
        <div className="flex items-center gap-2 text-sm text-amber-900">
          <UploadSimple size={18} weight="bold" />
          <span>Backup terugzetten? Upload een eerder gedownload <code className="font-mono text-xs bg-white px-1 rounded">.tar.gz</code> archief, of kies hieronder.</span>
        </div>
        <label className={`px-3 py-1.5 text-sm bg-white border border-amber-300 rounded-sm cursor-pointer hover:bg-amber-100 transition-colors flex items-center gap-2 ${uploading ? 'opacity-50 pointer-events-none' : ''}`} data-testid="backups-upload-btn">
          <UploadSimple size={14} />
          {uploading ? 'Bezig...' : 'Upload backup'}
          <input type="file" accept=".tar.gz,.gz" className="hidden" onChange={onUpload} data-testid="backups-upload-input" />
        </label>
      </div>

      {error && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded flex items-start gap-2 text-sm text-red-800" data-testid="backups-error">
          <Warning size={18} weight="fill" className="flex-shrink-0 mt-0.5" />
          <span>{error}</span>
        </div>
      )}
      {success && (
        <div className="mb-4 p-3 bg-emerald-50 border border-emerald-200 rounded flex items-start gap-2 text-sm text-emerald-800" data-testid="backups-success">
          <CheckCircle size={18} weight="fill" className="flex-shrink-0 mt-0.5" />
          <span>{success}</span>
        </div>
      )}

      {/* Settings + manual run card */}
      <div className="bg-white border border-zinc-200 rounded-sm p-5 mb-6">
        <div className="flex items-center gap-2 mb-4">
          <Clock size={18} className="text-zinc-600" />
          <h2 className="font-medium text-zinc-900">Planning & retentie</h2>
        </div>

        {settings ? (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div>
              <label className="text-xs uppercase tracking-wider text-zinc-500 mb-1 block">
                Automatisch
              </label>
              <button
                onClick={() => setSettings({ ...settings, enabled: !settings.enabled })}
                className={`w-full px-3 py-2 text-sm rounded-sm border transition-colors ${
                  settings.enabled
                    ? 'bg-emerald-50 border-emerald-300 text-emerald-800'
                    : 'bg-zinc-50 border-zinc-300 text-zinc-600'
                }`}
                data-testid="backups-enabled-toggle"
              >
                {settings.enabled ? '✓ Aan' : '✗ Uit'}
              </button>
            </div>

            <div>
              <label className="text-xs uppercase tracking-wider text-zinc-500 mb-1 block">
                Starttijd (Europe/Amsterdam)
              </label>
              <select
                value={settings.hour}
                onChange={(e) => setSettings({ ...settings, hour: parseInt(e.target.value, 10) })}
                className="w-full px-3 py-2 text-sm border border-zinc-300 rounded-sm"
                data-testid="backups-hour-select"
              >
                {Array.from({ length: 24 }, (_, i) => (
                  <option key={i} value={i}>
                    {String(i).padStart(2, '0')}:00
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="text-xs uppercase tracking-wider text-zinc-500 mb-1 block">
                Retentie (dagen)
              </label>
              <input
                type="number" min={1} max={365}
                value={settings.retention_days}
                onChange={(e) => setSettings({ ...settings, retention_days: parseInt(e.target.value, 10) || 14 })}
                className="w-full px-3 py-2 text-sm border border-zinc-300 rounded-sm"
                data-testid="backups-retention-input"
              />
            </div>

            <div className="flex items-end">
              <button
                onClick={saveSettings}
                disabled={savingSettings}
                className="w-full px-3 py-2 text-sm bg-zinc-900 text-white rounded-sm hover:bg-zinc-800 disabled:opacity-50 flex items-center justify-center gap-2"
                data-testid="backups-save-settings-btn"
              >
                <FloppyDisk size={14} />
                {savingSettings ? 'Bezig...' : 'Opslaan'}
              </button>
            </div>
          </div>
        ) : (
          <div className="text-sm text-zinc-400">Laden...</div>
        )}

        {settings && (
          <div className="mt-4 pt-4 border-t border-zinc-100 grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
            <div>
              <div className="text-xs text-zinc-500 uppercase tracking-wider">Laatste run</div>
              <div className={`mt-1 font-medium ${
                settings.last_run_status === 'ok' ? 'text-emerald-700' :
                settings.last_run_status === 'failed' ? 'text-red-700' :
                'text-zinc-500'
              }`}>
                {settings.last_run_at ? (
                  <>
                    {settings.last_run_status === 'ok' && '✓ '}
                    {settings.last_run_status === 'failed' && '✗ '}
                    {relTime(settings.last_run_at)}
                    {settings.last_run_size_bytes ? ` · ${fmtSize(settings.last_run_size_bytes)}` : ''}
                  </>
                ) : 'Nog nooit gedraaid'}
              </div>
              {settings.last_error && (
                <div className="text-xs text-red-600 mt-1 truncate" title={settings.last_error}>
                  {settings.last_error}
                </div>
              )}
            </div>
            <div>
              <div className="text-xs text-zinc-500 uppercase tracking-wider">Totaal backups</div>
              <div className="mt-1 font-medium text-zinc-900">{backups.length}</div>
            </div>
            <div className="flex items-end justify-end">
              <button
                onClick={runBackup}
                disabled={running}
                className="px-4 py-2 text-sm bg-blue-600 text-white rounded-sm hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2"
                data-testid="backups-run-now-btn"
              >
                <Play size={14} weight="fill" />
                {running ? 'Bezig...' : 'Maak backup nu'}
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Backup list */}
      <div className="bg-white border border-zinc-200 rounded-sm overflow-hidden" data-testid="backups-list">
        <div className="px-5 py-3 border-b border-zinc-100 flex items-center gap-2">
          <Database size={18} className="text-zinc-600" />
          <h2 className="font-medium text-zinc-900">Beschikbare backups</h2>
          <span className="text-xs text-zinc-400 ml-auto">
            /app/backups/
          </span>
        </div>

        {backups.length === 0 ? (
          <div className="p-12 text-center text-sm text-zinc-400">
            Nog geen backups. Klik op <strong>Maak backup nu</strong> voor je eerste backup.
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-zinc-50 text-xs uppercase tracking-wider text-zinc-500">
              <tr>
                <th className="px-5 py-2 text-left font-medium">Bestandsnaam</th>
                <th className="px-5 py-2 text-left font-medium">Datum</th>
                <th className="px-5 py-2 text-right font-medium">Grootte</th>
                <th className="px-5 py-2 text-right font-medium">Acties</th>
              </tr>
            </thead>
            <tbody>
              {backups.map((b) => (
                <tr key={b.filename} className="border-t border-zinc-100 hover:bg-zinc-50" data-testid={`backup-row-${b.filename}`}>
                  <td className="px-5 py-3 font-mono text-xs text-zinc-700">{b.filename}</td>
                  <td className="px-5 py-3 text-zinc-600">
                    <div>{fmtDateTime(b.created_at)}</div>
                    <div className="text-xs text-zinc-400">{relTime(b.created_at)}</div>
                  </td>
                  <td className="px-5 py-3 text-right font-medium text-zinc-700">{fmtSize(b.size_bytes)}</td>
                  <td className="px-5 py-3 text-right">
                    <div className="inline-flex items-center gap-1">
                      <button
                        onClick={() => openRestore(b.filename)}
                        className="p-1.5 hover:bg-amber-50 text-amber-700 rounded"
                        title="Herstellen vanuit deze backup"
                        data-testid={`backup-restore-${b.filename}`}
                      >
                        <ArrowCounterClockwise size={16} />
                      </button>
                      <button
                        onClick={() => downloadBackup(b.filename)}
                        className="p-1.5 hover:bg-blue-50 text-blue-600 rounded"
                        title="Download"
                        data-testid={`backup-download-${b.filename}`}
                      >
                        <DownloadSimple size={16} />
                      </button>
                      <button
                        onClick={() => deleteBackup(b.filename)}
                        className="p-1.5 hover:bg-red-50 text-red-600 rounded"
                        title="Verwijderen"
                        data-testid={`backup-delete-${b.filename}`}
                      >
                        <Trash size={16} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* History */}
      {history.length > 0 && (
        <div className="bg-white border border-zinc-200 rounded-sm overflow-hidden mt-6">
          <div className="px-5 py-3 border-b border-zinc-100">
            <h2 className="font-medium text-zinc-900 text-sm">Recente runs (log)</h2>
          </div>
          <table className="w-full text-xs">
            <thead className="bg-zinc-50 uppercase tracking-wider text-zinc-500">
              <tr>
                <th className="px-5 py-2 text-left font-medium">Datum</th>
                <th className="px-5 py-2 text-left font-medium">Trigger</th>
                <th className="px-5 py-2 text-right font-medium">Collecties</th>
                <th className="px-5 py-2 text-right font-medium">Documenten</th>
                <th className="px-5 py-2 text-right font-medium">Grootte</th>
                <th className="px-5 py-2 text-right font-medium">Duur</th>
                <th className="px-5 py-2 text-right font-medium">Status</th>
              </tr>
            </thead>
            <tbody>
              {history.map((h, i) => (
                <tr key={i} className="border-t border-zinc-100">
                  <td className="px-5 py-2 text-zinc-600">{fmtDateTime(h.created_at)}</td>
                  <td className="px-5 py-2 text-zinc-500 font-mono truncate max-w-[180px]">{h.trigger || '—'}</td>
                  <td className="px-5 py-2 text-right text-zinc-700">{h.collections ?? '—'}</td>
                  <td className="px-5 py-2 text-right text-zinc-700">{h.docs?.toLocaleString('nl-NL') ?? '—'}</td>
                  <td className="px-5 py-2 text-right text-zinc-700">{fmtSize(h.size_bytes)}</td>
                  <td className="px-5 py-2 text-right text-zinc-500">{h.duration_ms ? `${(h.duration_ms / 1000).toFixed(1)}s` : '—'}</td>
                  <td className="px-5 py-2 text-right">
                    {h.status === 'ok' ? (
                      <span className="text-emerald-700">✓ OK</span>
                    ) : (
                      <span className="text-red-700">✗ {h.status || 'fail'}</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Restore confirmation modal */}
      {restoreModal && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" data-testid="restore-modal">
          <div className="bg-white rounded-sm shadow-xl w-full max-w-3xl max-h-[90vh] overflow-y-auto">
            <div className="px-6 py-4 border-b border-zinc-100 flex items-center justify-between">
              <h3 className="font-semibold text-lg text-zinc-900 flex items-center gap-2">
                <ArrowCounterClockwise size={20} className="text-amber-700" />
                Backup herstellen
              </h3>
              <button onClick={() => { setRestoreModal(null); setConfirmText(''); }} className="p-1 hover:bg-zinc-100 rounded" data-testid="restore-modal-close">
                <X size={18} />
              </button>
            </div>

            <div className="px-6 py-4 space-y-4">
              <div className="p-4 bg-red-50 border border-red-300 rounded-sm">
                <div className="font-semibold text-red-900 mb-1 flex items-center gap-2">
                  <Warning size={18} weight="fill" />
                  DESTRUCTIEVE ACTIE
                </div>
                <p className="text-sm text-red-800 leading-relaxed">
                  Alle huidige data wordt <strong>vervangen</strong> door de inhoud van deze backup.
                  Bestaande documenten in MongoDB worden gedropt en opnieuw aangemaakt.
                  Bestanden in <code className="text-xs">/app/backend/uploads/</code> worden overschreven.
                </p>
                <p className="text-sm text-red-800 mt-2">
                  ✓ <strong>Veiligheidsnet</strong>: er wordt eerst automatisch een backup gemaakt van de
                  huidige staat (trigger=<code className="text-xs">pre-restore</code>) — terugdraaien blijft mogelijk.
                </p>
              </div>

              <div>
                <div className="text-sm font-medium text-zinc-700 mb-2">
                  Backup: <code className="text-xs bg-zinc-100 px-2 py-0.5 rounded">{restoreModal.filename}</code>
                </div>
                <div className="text-xs text-zinc-500 mb-3">
                  {restoreModal.preview?.total_docs?.toLocaleString('nl-NL')} documenten · {restoreModal.preview?.total_files} bestanden ·
                  gemaakt op {fmtDateTime(restoreModal.preview?.manifest?.created_at)}
                </div>

                {restoreModal.preview?.collections?.length > 0 && (
                  <div className="border border-zinc-200 rounded-sm max-h-72 overflow-y-auto">
                    <table className="w-full text-xs">
                      <thead className="bg-zinc-50 sticky top-0">
                        <tr>
                          <th className="px-3 py-1.5 text-left font-medium text-zinc-500">Collectie</th>
                          <th className="px-3 py-1.5 text-right font-medium text-zinc-500">Nu</th>
                          <th className="px-3 py-1.5 text-right font-medium text-zinc-500">Backup</th>
                          <th className="px-3 py-1.5 text-right font-medium text-zinc-500">Δ</th>
                        </tr>
                      </thead>
                      <tbody>
                        {restoreModal.preview.collections.map((c) => {
                          const diff = (c.backup_count || 0) - (c.current_count || 0);
                          return (
                            <tr key={c.name} className="border-t border-zinc-100" data-testid={`restore-row-${c.name}`}>
                              <td className="px-3 py-1 font-mono text-zinc-700">{c.name}</td>
                              <td className="px-3 py-1 text-right text-zinc-500">{(c.current_count || 0).toLocaleString('nl-NL')}</td>
                              <td className="px-3 py-1 text-right text-zinc-700 font-medium">{(c.backup_count || 0).toLocaleString('nl-NL')}</td>
                              <td className={`px-3 py-1 text-right font-medium ${diff > 0 ? 'text-emerald-700' : diff < 0 ? 'text-red-700' : 'text-zinc-400'}`}>
                                {diff > 0 ? `+${diff}` : diff}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-zinc-700 mb-1">
                  Typ <code className="bg-zinc-100 px-1.5 py-0.5 rounded text-red-700 font-bold">RESTORE</code> ter bevestiging:
                </label>
                <input
                  type="text"
                  value={confirmText}
                  onChange={(e) => setConfirmText(e.target.value)}
                  placeholder="RESTORE"
                  className="w-full px-3 py-2 text-sm border border-zinc-300 rounded-sm font-mono"
                  data-testid="restore-confirm-input"
                />
              </div>
            </div>

            <div className="px-6 py-4 bg-zinc-50 border-t border-zinc-100 flex items-center justify-end gap-2">
              <button
                onClick={() => { setRestoreModal(null); setConfirmText(''); }}
                className="px-4 py-2 text-sm border border-zinc-300 rounded-sm hover:bg-white"
                data-testid="restore-cancel-btn"
              >
                Annuleren
              </button>
              <button
                onClick={doRestore}
                disabled={confirmText !== 'RESTORE' || restoreLoading}
                className="px-4 py-2 text-sm bg-red-600 text-white rounded-sm hover:bg-red-700 disabled:bg-zinc-300 disabled:cursor-not-allowed flex items-center gap-2"
                data-testid="restore-confirm-btn"
              >
                <ArrowCounterClockwise size={14} weight="bold" />
                {restoreLoading ? 'Bezig met herstellen...' : 'Herstellen'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
